(() => {
  if (window.OrtluxSticky) {
    window.OrtluxSticky.init();
    return;
  }
  let active, controller;
  const on = (target, event, handler, options = {}) =>
    target?.addEventListener(event, handler, {
      ...(typeof options === "boolean" ? { capture: options } : options),
      signal: controller.signal,
    });
  async function init() {
    const next = document.querySelector(".commerce-shell");
    if (!next || active === next) return;
    controller?.abort();
    active = next;
    controller = new AbortController();
    const instance = controller;
    await setup(instance);
  }
  async function setup(controller) {
    // One passive scroll listener. Geometry is read only during setup/viewport changes,
    // never in that listener or its animation-frame direction callback.

    await document.fonts.ready;
    if (controller.signal.aborted) return;
    const root = document.documentElement;
    const stage = document.querySelector(".sticky-top");
    if (!stage) return;
    const sentinel =
      stage.parentElement.querySelector("#header-sentinel") || document.getElementById("header-sentinel");
    // Horizon's section-group wrapper is sticky. Keep the zero-height observer
    // target in document flow so it can leave the viewport independently.
    const headerGroup = document.getElementById("header-group");
    if (headerGroup && headerGroup.contains(sentinel)) headerGroup.before(sentinel);
    const announcement = document.querySelector(".announcement");
    const header = document.querySelector(".commerce-header");
    const shell = document.querySelector(".commerce-shell");
    const logo = header.querySelector(".commerce-logo");
    const tools = header.querySelector(".commerce-tools");
    const browse = shell.querySelector(".mobile-browse");
    const forms = [...header.querySelectorAll(".site-search")];
    const searchSlots = [...header.querySelectorAll(".header-search")];
    const searchButtons = [...header.querySelectorAll(".compact-search-trigger")];
    const mobile = matchMedia("(max-width:700px)");
    if (!("IntersectionObserver" in window)) return; // Native full sticky header remains usable.
    let geometry,
      pastAnnouncement = false,
      atTop = true,
      compact = false,
      dismissed = root.classList.contains("announcement-dismissed");
    let expanded = false,
      direction = "up",
      previousY = scrollY,
      distance = 0,
      framePending = false;
    let topObserver, announcementObserver;
    controller.signal.addEventListener(
      "abort",
      () => {
        topObserver?.disconnect();
        announcementObserver?.disconnect();
        sentinel.remove();
        root.classList.remove("header-ready", "header-compact", "search-expanded");
      },
      { once: true },
    );
    const setPx = (name, value) => root.style.setProperty(name, `${value}px`);
    const syncPadding = () =>
      setPx(
        "--header-active-height",
        (compact ? geometry.compact : geometry.full) + (dismissed ? 0 : geometry.announcement),
      );
    const closeSearch = () => forms.forEach((form) => form.dispatchEvent(new Event("search:close")));
    const syncSearch = () => {
      searchSlots.forEach((slot) => slot.setAttribute("aria-hidden", String(compact && !expanded)));
      searchButtons.forEach((button) => (button.inert = !compact || expanded));
      root.classList.toggle("search-expanded", compact && expanded);
    };
    const closeDetails = () => {
      [...shell.querySelectorAll("details[open]")].reverse().forEach((detail) => {
        const summary = detail.querySelector("summary");
        if (detail.contains(document.activeElement) && document.activeElement !== summary)
          summary.focus({ preventScroll: true });
        detail.open = false;
      });
    };
    const setCompact = (value) => {
      if (!geometry || value === compact) return;
      if (
        value &&
        (shell.querySelector(".mega-item[open],.locale-picker[open],.mobile-browse[open]") ||
          document.activeElement?.closest(".site-search"))
      )
        return;
      if (value) {
        closeDetails();
        closeSearch();
      }
      const focusedSearchButton = document.activeElement?.closest(".compact-search-trigger");
      compact = value;
      if (!value) expanded = false;
      root.classList.toggle("header-compact", compact);
      syncSearch();
      syncPadding();
      if (!value && focusedSearchButton) {
        forms
          .find(
            (form) =>
              form.querySelector(".search-panel").id === focusedSearchButton.getAttribute("aria-controls"),
          )
          .querySelector("input")
          .focus({ preventScroll: true });
      }
    };
    const dismissAnnouncement = () => {
      if (dismissed) return;
      dismissed = true;
      if (announcement.contains(document.activeElement)) logo.focus({ preventScroll: true });
      announcement.inert = true;
      announcement.setAttribute("aria-hidden", "true");
      root.classList.add("announcement-dismissed");
      syncPadding();
    };
    function measure() {
      // Cached untransformed layout dimensions. Recomputed only on an actual resize.
      const height = (element) => parseFloat(getComputedStyle(element).height);
      const ah = height(announcement);
      const hh = height(header);
      const navHeight = mobile.matches
        ? height(browse)
        : height(shell.querySelector(".commerce-navigation-row"));
      const total = hh + navHeight;
      const compactTotal = mobile.matches ? Math.min(64, innerHeight * 0.12) : hh * 0.6 + navHeight;
      const toolbar = mobile.matches ? compactTotal * 0.55 : hh * 0.6;
      const scale = toolbar / hh;
      const logoCenter = logo.offsetTop + logo.offsetHeight / 2;
      const toolsCenter = tools.offsetTop + tools.offsetHeight / 2;
      const slot = header.querySelector(mobile.matches ? ".mobile-search-slot" : ".desktop-search-slot");
      const searchCenter = slot.offsetTop + slot.offsetHeight / 2;
      const buttonSize = mobile.matches ? 32 : 44;
      geometry = {
        announcement: ah,
        full: total,
        compact: compactTotal,
        toolbar,
      };
      setPx("--announcement-height", ah);
      setPx("--header-full-height", total);
      setPx("--header-compact-height", compactTotal);
      root.style.setProperty("--toolbar-scale", scale);
      root.style.setProperty("--toolbar-inverse", 1 / scale);
      root.style.setProperty("--logo-scale", mobile.matches ? 0.75 : 0.8);
      root.style.setProperty("--tools-scale", mobile.matches ? 0.75 : 1);
      setPx("--logo-shift-y", toolbar / 2 / scale - logoCenter);
      setPx("--tools-shift-y", toolbar / 2 / scale - toolsCenter);
      setPx(
        "--search-shift-y",
        (mobile.matches ? compactTotal + 8 + slot.offsetHeight / 2 : toolbar / 2) / scale - searchCenter,
      );
      setPx(
        "--search-button-x",
        mobile.matches
          ? logo.offsetLeft + logo.offsetWidth * 0.75 + 10
          : slot.offsetLeft + slot.offsetWidth - buttonSize,
      );
      setPx("--search-button-y", (toolbar - buttonSize) / 2 / scale);
      setPx("--navigation-shift-y", toolbar - hh);
      const browseScale = mobile.matches ? (compactTotal - toolbar) / navHeight : 1;
      root.style.setProperty("--browse-scale", browseScale);
      root.style.setProperty("--browse-inverse", 1 / browseScale);
      // Only initial-viewport images are promoted; hidden menu imagery stays lazy.
      if (window.scrollY === 0)
        document.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          if (image.closest("details") || image.closest(".hero-visual")) return;
          const rect = image.getBoundingClientRect();
          if (rect.width && rect.height && rect.top < innerHeight && rect.bottom > 0) image.loading = "eager";
        });
      syncPadding();
      topObserver?.disconnect();
      announcementObserver?.disconnect();
      topObserver = new IntersectionObserver(([entry]) => {
        atTop = entry.isIntersecting;
        if (atTop) {
          setCompact(false);
          distance = 0;
        }
      });
      announcementObserver = new IntersectionObserver(
        ([entry]) => {
          pastAnnouncement = !entry.isIntersecting;
          if (pastAnnouncement) {
            dismissAnnouncement();
            if (direction === "down") setCompact(true);
          }
        },
        { rootMargin: `${ah}px 0px 0px 0px` },
      );
      topObserver.observe(sentinel);
      announcementObserver.observe(sentinel);
    }
    measure();
    root.classList.add("header-ready");
    announcement.inert = dismissed;
    announcement.setAttribute("aria-hidden", String(dismissed));
    const announcementClose = announcement.querySelector(".announcement-close");
    on(announcementClose, "click", () => {
      dismissAnnouncement();
      logo.focus({ preventScroll: true });
    });
    announcementClose.removeAttribute("disabled");
    searchButtons.forEach((button) => {
      button.hidden = false;
      on(button, "click", () => {
        expanded = true;
        syncSearch();
        const form = forms.find(
          (form) => form.querySelector(".search-panel").id === button.getAttribute("aria-controls"),
        );
        form.querySelector("input").focus({ preventScroll: true });
      });
    });
    syncSearch();
    forms.forEach((form) =>
      on(form, "keydown", (event) => {
        if (event.key !== "Escape" || !compact) return;
        expanded = false;
        closeSearch();
        syncSearch();
        searchButtons
          .find((button) => button.getAttribute("aria-controls") === form.querySelector(".search-panel").id)
          .focus({ preventScroll: true });
      }),
    );
    on(
      shell,
      "toggle",
      (event) => {
        if (event.target.matches(".mega-item,.locale-picker,.mobile-browse") && event.target.open)
          setCompact(false);
      },
      true,
    );
    on(shell, "header:interaction", () => setCompact(false));
    // Accumulate only movement in the same direction; sub-8px reversals do not toggle.
    on(
      window,
      "scroll",
      () => {
        if (framePending) return;
        framePending = true;
        requestAnimationFrame(() => {
          if (controller.signal.aborted) return;
          framePending = false;
          const y = Math.max(0, window.scrollY);
          const delta = y - previousY;
          previousY = y;
          if (!delta) return;
          distance = Math.sign(delta) === Math.sign(distance) ? distance + delta : delta;
          if (Math.abs(distance) < 8) return;
          direction = distance > 0 ? "down" : "up";
          distance = 0;
          if (direction === "up") setCompact(false);
          else if (!atTop && pastAnnouncement) setCompact(true);
        });
      },
      { passive: true },
    );
    let resizeFrame;
    on(window, "resize", () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        if (controller.signal.aborted) return;
        measure();
        syncSearch();
      });
    });
    // Focus correction is deliberately outside the scrolling direction callback.
    on(document, "focusin", (event) => {
      const target = event.target;
      if (
        !(target instanceof HTMLElement) ||
        stage.contains(target) ||
        target.closest("dialog") ||
        target.classList.contains("skip")
      )
        return;
      setCompact(false);
      requestAnimationFrame(() => {
        if (controller.signal.aborted) return;
        const rect = target.getBoundingClientRect();
        const protectedTop =
          (compact ? geometry.compact : geometry.full) + (dismissed ? 0 : geometry.announcement) + 8;
        if (rect.top < protectedTop) window.scrollBy({ top: rect.top - protectedTop, behavior: "instant" });
      });
    });
  }
  window.OrtluxSticky = { init };
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.querySelector(".commerce-shell")) init();
  });
  document.addEventListener("shopify:section:unload", (event) => {
    if (active && event.target.contains(active)) {
      controller?.abort();
      active = null;
    }
  });
  init();
})();
